<template>
    <div>
        <h5 class="text-s text-center"><strong>Namibia Credit Report</strong></h5>

        <!-- Add Decision Engine -->
        <div class="form-group mt-3">
            <label class="form-label text-xs fw-bold d-block">Include Instant Decision Engine
                <span class="text-muted fw-normal">(additional charges apply)</span>
            </label>
            <div class="d-flex gap-4">
                <div class="form-check">
                    <input class="form-check-input" type="radio" name="add_decision_engine" :value="true" v-model="add_decision_engine">
                    <label class="form-check-label text-xs">
                        Yes
                    </label>
                </div>
                <div class="form-check">
                    <input class="form-check-input" type="radio" name="add_decision_engine" :value="false" v-model="add_decision_engine">
                    <label class="form-check-label text-xs">
                        No
                    </label>
                </div>
            </div>
        </div>

        <!-- ID Type and ID Number in same row -->
        <div class="form-group mt-3">
            <div class="row">
                <!-- ID Type -->
                <div class="col-4 pe-2">
                    <label class="form-label text-xs fw-bold">ID Type <span class="text-danger fw-bold">*</span></label>
                    <select
                        v-model="id_number_type"
                        class="form-select text-xs rounded-small p-3 shadow-sm bordered-input">
                        <option value="NationalID">National ID</option>
                        <option value="PassportNumber">Passport</option>
                    </select>
                </div>
                <!-- ID Number -->
                <div class="col-8 ps-2">
                    <label class="form-label text-xs fw-bold">
                        {{ id_number_type === 'PassportNumber' ? 'Passport Number' : 'ID Number' }}
                        <span class="text-danger fw-bold">*</span>
                    </label>
                    <input
                        type="text"
                        class="form-control text-xs rounded-small p-3 shadow-sm bordered-input"
                        v-model="id_number"
                        :placeholder="id_number_type === 'PassportNumber' ? 'Enter Passport Number' : 'Enter ID Number (11 digits)'"
                        :maxlength="id_number_type === 'PassportNumber' ? 20 : 13"
                        @input="handle_id_input">
                    <small class="text-danger fw-bold text-xs d-none id-number-message">
                        Please fill in this field
                    </small>
                    <small class="text-danger fw-bold text-xs d-none id-invalid-message">
                        Please enter a valid {{ id_number_type === 'PassportNumber' ? 'passport number' : 'ID number' }}
                    </small>
                </div>
            </div>
        </div>

        <!-- First Name and Last Name in same row -->
        <div class="form-group mt-3">
            <div class="row">
                <!-- First Name -->
                <div class="col-6 pe-2">
                    <label class="form-label text-xs fw-bold">First Name <span class="text-danger fw-bold">*</span></label>
                    <input
                        type="text"
                        class="form-control text-xs rounded-small p-3 shadow-sm bordered-input"
                        v-model="first_name"
                        placeholder="Enter First Name"
                        maxlength="50">
                    <small class="text-danger fw-bold text-xs d-none first-name-message">
                        Please fill in this field
                    </small>
                </div>
                <!-- Last Name -->
                <div class="col-6 ps-2">
                    <label class="form-label text-xs fw-bold">Last Name <span class="text-danger fw-bold">*</span></label>
                    <input
                        type="text"
                        class="form-control text-xs rounded-small p-3 shadow-sm bordered-input"
                        v-model="last_name"
                        placeholder="Enter Last Name"
                        maxlength="50">
                    <small class="text-danger fw-bold text-xs d-none last-name-message">
                        Please fill in this field
                    </small>
                </div>
            </div>
        </div>

        <div class="form-group mt-3">
            <div class="row">
                <!-- Phone Number with Country Code -->
                <div class="col-6 pe-2">
                    <label class="form-label text-xs fw-bold">Phone Number <span class="text-danger fw-bold">*</span></label>
                    <PhoneInput
                        ref="phoneInput"
                        v-model="phone_number"
                        input-class="form-control text-xs rounded-small p-3 shadow-sm w-100"
                        :show-errors="true"
                        empty-message="Please fill in this field"
                        :only-countries="phoneCountries"
                    />
                </div>
                <!-- Reason of Inquiry -->
                <div class="col-6 pe-2">
                    <label class="form-label text-xs fw-bold">Reason of Inquiry</label>
                    <select
                        class="form-select text-xs rounded-small p-3 shadow-sm bordered-input"
                        v-model="reason_of_inquiry">
                        <option v-for="reason in inquiry_reasons" :key="reason.value" :value="reason.value">
                            {{ reason.label }}
                        </option>
                    </select>
                    <small class="text-danger fw-bold text-xs d-none reason-message">
                        Please select a reason
                    </small>
                </div>
            </div>
        </div>

        <div class="col text-end">
            <button
                type="submit"
                class="btn bg-green btn-sm text-xs shadow rounded-small mt-2 fw-bold"
                @click.prevent="submit_form">
                Submit
            </button>
        </div>
    </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

export default {
    name: 'CreditReportNamibiaForm',
    components: {
        PhoneInput
    },
    data() {
        return {
            id_number: '',
            id_number_type: 'NationalID',
            first_name: '',
            last_name: '',
            phone_number: '',
            reason_of_inquiry: 'CustomerInquiry',
            add_decision_engine: false,
            error: '',
            inquiry_reasons: [
                { value: "CustomerInquiry", label: "Customer Inquiry" }
            ]
        };
    },
    emits: ['service_status_complete'],
    computed: {
        phoneCountries() {
            return this.$store.getters.get_branding?.phone_countries || [];
        },
        full_phone_number() {
            if (this.$refs.phoneInput) {
                return this.$refs.phoneInput.getFullNumber();
            }
            return this.phone_number;
        }
    },
    methods: {
        validatePhoneNumber() {
            return this.$refs.phoneInput?.validate() ?? false;
        },
        handle_id_input(event) {
            if (this.id_number_type === 'NationalID') {
                // For National ID, only allow numbers
                this.id_number = event.target.value.replace(/[^0-9]/g, '');
            } else {
                // For Passport, allow alphanumeric
                this.id_number = event.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            }
        },
        validate_namibian_id(id_number) {
            if (this.id_number_type === 'NationalID') {
                if (!id_number || id_number.length < 11) {
                    return false;
                }
                const namibian_id_regex = /^\d{11,13}$/;
                return namibian_id_regex.test(id_number);
            } else {
                const passport_regex = /^[A-Z0-9]{6,20}$/;
                return passport_regex.test(id_number);
            }
        },
        show_error(selector) {
            $(selector).removeClass('d-none');
            setTimeout(() => {
                $(selector).addClass('d-none');
            }, 3000);
        },
        validate_fields() {
            let is_valid = true;

            $('.id-number-message, .id-invalid-message, .first-name-message, .last-name-message, .reason-message').addClass('d-none');

            if (!this.id_number.trim()) {
                $('.id-number-message').removeClass('d-none');
                setTimeout(() => $('.id-number-message').addClass('d-none'), 3000);
                is_valid = false;
            } else if (!this.validate_namibian_id(this.id_number)) {
                $('.id-invalid-message').removeClass('d-none');
                setTimeout(() => $('.id-invalid-message').addClass('d-none'), 3000);
                is_valid = false;
            }

            if (!this.first_name.trim()) {
                $('.first-name-message').removeClass('d-none');
                setTimeout(() => $('.first-name-message').addClass('d-none'), 3000);
                is_valid = false;
            }

            if (!this.last_name.trim()) {
                $('.last-name-message').removeClass('d-none');
                setTimeout(() => $('.last-name-message').addClass('d-none'), 3000);
                is_valid = false;
            }
            if (!this.validatePhoneNumber()) {
                is_valid = false;
            }
            if (!this.reason_of_inquiry) {
                $('.reason-message').removeClass('d-none');
                setTimeout(() => $('.reason-message').addClass('d-none'), 3000);
                is_valid = false;
            }

            return is_valid;
        },
        submit_form() {
            if (!this.validate_fields()) {
                return;
            }
            this.submit_namibia_credit_report();
        },
        submit_namibia_credit_report() {
            const TOKEN = localStorage.getItem('token');
            const API_URL = `${API_BASE_URL}/verify_api/instant_verify_api/credit_report_namibia/`;

            this.error = '';

            $.ajax({
                url: API_URL,
                method: 'POST',
                contentType: 'application/json',
                headers: {
                    Authorization: `Token ${TOKEN}`
                },
                data: JSON.stringify({
                    id_number: this.id_number,
                    id_number_type: this.id_number_type,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    phone_number: this.full_phone_number,
                    reason_of_inquiry: this.reason_of_inquiry,
                    add_decision_engine: this.add_decision_engine
                }),
                beforeSend: () => {
                    Swal.fire({
                        text: 'Loading ...',
                        allowEscapeKey: false,
                        allowOutsideClick: false,
                        didOpen: () => Swal.showLoading()
                    });
                },
                success: () => {
                    Swal.close();
                    Swal.fire({
                        icon: 'success',
                        title: 'Successful',
                        text: 'Namibia Credit Report retrieved successfully',
                        timer: 3000,
                        timerProgressBar: true,
                        showConfirmButton: false
                    }).then(() => {
                        this.$emit('service_status_complete');
                        this.id_number = '';
                        this.id_number_type = 'NationalID';
                        this.first_name = '';
                        this.last_name = '';
                        this.phone_number = '';
                        this.reason_of_inquiry = 'CustomerInquiry';
                        this.add_decision_engine = false;
                    });
                },
                error: (error) => {
                    Swal.close();
                    let error_message = 'An error occurred while retrieving Namibia credit report.';
                    if (error.responseJSON && error.responseJSON.error) {
                        error_message = error.responseJSON.error;
                    } else if (error.responseJSON && error.responseJSON.message) {
                        error_message = error.responseJSON.errors;
                    }
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error_message,
                        confirmButtonText: 'OK'
                    });
                }
            });
        }
    },
    mounted() {
        if (!validateToken(this.$router)) {
            return;
        }
    }
};
</script>